/**
 * Recently Viewed Products Manager
 *
 * TESTING INSTRUCTIONS:
 * 1. Open browser console (F12)
 * 2. Test if storage is working:
 *    window.recentlyViewedManager.debugStorage()
 * 3. Add test products:
 *    window.recentlyViewedManager.testAddMultipleProducts()
 * 4. View stored products:
 *    window.recentlyViewedManager.getRecentlyViewed()
 * 5. Visit actual product pages to track real products
 * 6. Navigate to a page with the "Recently Viewed Products" section to see them displayed
 *
 * IMPORTANT: Won't work in Shopify theme editor (sandboxed iframe)
 * Test on actual storefront: yourstore.myshopify.com (use preview theme)
 */

// Prevent duplicate class declaration - use global window object
if (typeof window.RecentlyViewedManager === 'undefined') {
  window.RecentlyViewedManager = class RecentlyViewedManager {
    constructor() {
      this.storageKey = 'shopify_recently_viewed';
      this.maxItems = 12; // Maximum number of recently viewed products to store
      this.storageAvailable = this.checkStorageAvailability();
    }

    /**
     * Check if localStorage is available and accessible
     * Returns false in sandboxed contexts (like theme editor preview)
     */
    checkStorageAvailability() {
      try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        const retrieved = localStorage.getItem(test);
        localStorage.removeItem(test);

        if (retrieved === test) {
          console.log('✅ localStorage is accessible and working');
          return true;
        } else {
          console.warn('⚠️ localStorage write/read mismatch');
          return false;
        }
      } catch (error) {
        console.error('❌ localStorage is not available:', error.name, error.message);
        return false;
      }
    }

    init() {
      console.log('🚀 Recently Viewed Manager initialized (v2.0)');
      console.log('📍 Is product page?', this.isProductPage());
      console.log('📍 Body classes:', document.body.className);
      console.log('💾 Storage available?', this.storageAvailable);

      if (!this.storageAvailable) {
        console.warn(
          '⚠️ Recently viewed feature disabled - localStorage not accessible (theme editor/sandboxed context)'
        );
        return;
      }

      // Show current items in storage
      const currentItems = this.getRecentlyViewed();
      console.log(
        '📦 Currently stored products:',
        currentItems.length,
        currentItems.map((p) => p.title)
      );

      // Track current product on product pages
      if (this.isProductPage()) {
        this.trackProduct();
      } else {
        console.log('⚠️ Not a product page, skipping tracking');
        console.log('💡 TIP: Visit a product page to start tracking!');
      }
    }

    isProductPage() {
      // Check multiple indicators for product page
      const hasTemplateClass = document.body.classList.contains('template-product');
      const hasProductJson = !!document.querySelector('[data-product-json]');
      const hasProductInfo = !!document.querySelector('product-info');
      const hasProductMeta = !!document.querySelector('meta[property="product:id"]');
      const urlIsProduct = window.location.pathname.includes('/products/');

      console.log('🔍 Product page detection:', {
        hasTemplateClass,
        hasProductJson,
        hasProductInfo,
        hasProductMeta,
        urlIsProduct,
      });

      // Return true if any of these conditions are met
      return hasTemplateClass || hasProductJson || hasProductInfo || hasProductMeta || urlIsProduct;
    }

    trackProduct() {
      console.log('🔄 Attempting to track product...');

      // Get product data from meta tags or data attributes
      const productData = this.getProductData();
      console.log('📊 Product data found:', productData);

      if (productData && productData.id) {
        this.addToRecentlyViewed(productData);
        console.log('✅ Product tracked successfully:', productData.title, 'ID:', productData.id);

        // Verify it was saved
        const saved = this.getRecentlyViewed();
        console.log('✔️ Verification - Total products in storage:', saved.length);
      } else {
        console.error('❌ Could not track product - no product data found');
        console.log(
          '🔍 Debugging: Check if [data-product-json] element exists:',
          !!document.querySelector('[data-product-json]')
        );
      }
    }

    getProductData() {
      console.log('🔍 Searching for product data...');

      // Try to get product data from various sources
      const productJson = document.querySelector('[data-product-json]');
      console.log('📄 Product JSON element found?', !!productJson);

      if (productJson) {
        try {
          const textContent = productJson.textContent.trim();
          console.log('📝 Raw JSON content length:', textContent.length);

          if (!textContent) {
            console.error('❌ Product JSON element is empty');
          } else {
            const data = JSON.parse(textContent);
            console.log('✅ Product data from JSON:', data);

            // Ensure we have the minimum required data
            if (data && data.id && data.title) {
              return {
                id: String(data.id), // Ensure ID is a string for consistency
                title: data.title,
                url: data.url || window.location.pathname,
                image: data.image || data.featured_image || '',
                price: data.price || '',
              };
            } else {
              console.warn('⚠️ Product data missing required fields:', data);
            }
          }
        } catch (e) {
          console.error('❌ Error parsing product JSON:', e);
          console.log('Content:', productJson.textContent);
        }
      }

      // Fallback: try to get from meta tags
      console.log('🔄 Trying fallback: meta tags');
      const productIdMeta = document.querySelector('meta[property="product:id"]');
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogPrice = document.querySelector('meta[property="product:price:amount"]');

      console.log('Meta tags found:', {
        productId: !!productIdMeta,
        title: !!ogTitle,
        image: !!ogImage,
        url: !!ogUrl,
        price: !!ogPrice,
      });

      if (productIdMeta && ogTitle) {
        const data = {
          id: String(productIdMeta.content), // Ensure ID is a string
          title: ogTitle.content,
          url: ogUrl ? ogUrl.content : window.location.href,
          image: ogImage ? ogImage.content : '',
          price: ogPrice ? `₹${ogPrice.content}` : '',
        };
        console.log('✅ Product data from meta tags:', data);
        return data;
      }

      // Last resort: try data-product-id attribute on product-info element
      const productInfo = document.querySelector('product-info[data-product-id]');
      if (productInfo) {
        const productId = productInfo.getAttribute('data-product-id');
        const productUrl = productInfo.getAttribute('data-url') || window.location.pathname;

        // Try to get title from page
        const titleElement = document.querySelector('.product__title, [itemprop="name"]');
        const title = titleElement ? titleElement.textContent.trim() : document.title;

        // Try to get image from page
        const imageElement = document.querySelector('.product__media img, [data-media-id] img');
        const image = imageElement ? imageElement.src : '';

        // Try to get price from page
        const priceElement = document.querySelector('.price__regular .price-item--regular, [data-price]');
        const price = priceElement ? priceElement.textContent.trim() : '';

        if (productId && title) {
          const data = {
            id: String(productId),
            title: title,
            url: productUrl,
            image: image,
            price: price,
          };
          console.log('✅ Product data from DOM attributes:', data);
          return data;
        }
      }

      console.error('❌ No product data found');
      return null;
    }

    getRecentlyViewed() {
      if (!this.storageAvailable) {
        return [];
      }

      try {
        const items = localStorage.getItem(this.storageKey);
        return items ? JSON.parse(items) : [];
      } catch (error) {
        console.error('Error reading recently viewed:', error);
        return [];
      }
    }

    addToRecentlyViewed(product) {
      console.log('💾 addToRecentlyViewed called with:', product);

      if (!this.storageAvailable) {
        console.error('❌ Cannot save - localStorage not available (sandboxed context)');
        return;
      }

      try {
        let recentlyViewed = this.getRecentlyViewed();
        console.log('📋 Current items before adding:', recentlyViewed.length);

        // Ensure product ID is a string for consistent comparison
        const productId = String(product.id);

        // Remove if already exists (to move it to the front)
        recentlyViewed = recentlyViewed.filter((item) => String(item.id) !== productId);

        // Add to the beginning
        recentlyViewed.unshift({
          id: productId,
          title: product.title,
          url: product.url,
          image: product.image,
          price: product.price,
          viewedAt: new Date().toISOString(),
        });

        // Keep only the max number of items
        if (recentlyViewed.length > this.maxItems) {
          recentlyViewed = recentlyViewed.slice(0, this.maxItems);
        }

        // Save to localStorage
        console.log('💾 Saving to localStorage:', this.storageKey);
        localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));

        // Verify it was saved
        const verification = localStorage.getItem(this.storageKey);
        console.log('✔️ Saved successfully. Length:', verification?.length, 'bytes');

        console.log(
          `✅ Recently viewed updated (${recentlyViewed.length} total):`,
          recentlyViewed.map((p) => p.title)
        );

        // Dispatch event for UI updates
        window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
      } catch (error) {
        console.error('❌ Error saving recently viewed:', error);
      }
    }

    clearRecentlyViewed() {
      if (!this.storageAvailable) {
        console.warn('⚠️ Cannot clear recently viewed - localStorage not available');
        return;
      }

      try {
        localStorage.removeItem(this.storageKey);
        window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
        console.log('🗑️ Recently viewed cleared');
      } catch (error) {
        console.error('Error clearing recently viewed:', error);
      }
    }

    // Manual test function
    testAddProduct() {
      if (!this.storageAvailable) {
        console.error('❌ Cannot test - localStorage not available');
        return [];
      }

      const testProduct = {
        id: '12345',
        title: 'Test Product ' + Date.now(),
        url: '/products/test',
        image: 'https://via.placeholder.com/500',
        price: '₹999',
      };
      this.addToRecentlyViewed(testProduct);
      console.log('🧪 Test product added:', testProduct);
      return this.getRecentlyViewed();
    }

    // Quick test to add multiple products
    testAddMultipleProducts() {
      if (!this.storageAvailable) {
        console.error('❌ Cannot test - localStorage not available');
        return [];
      }

      const products = [
        {
          id: '1001',
          title: 'Gold Necklace',
          url: '/products/gold-necklace',
          image: 'https://via.placeholder.com/500',
          price: '₹2,999',
        },
        {
          id: '1002',
          title: 'Silver Ring',
          url: '/products/silver-ring',
          image: 'https://via.placeholder.com/500',
          price: '₹1,499',
        },
        {
          id: '1003',
          title: 'Diamond Earrings',
          url: '/products/diamond-earrings',
          image: 'https://via.placeholder.com/500',
          price: '₹5,999',
        },
        {
          id: '1004',
          title: 'Pearl Bracelet',
          url: '/products/pearl-bracelet',
          image: 'https://via.placeholder.com/500',
          price: '₹3,499',
        },
      ];

      products.forEach((p) => this.addToRecentlyViewed(p));
      console.log('🧪 Added', products.length, 'test products');
      return this.getRecentlyViewed();
    }

    // Debug helper to check localStorage
    debugStorage() {
      console.log('🔍 Debug Storage Info:');
      console.log('Storage Key:', this.storageKey);
      console.log('Storage Available:', this.storageAvailable);

      if (!this.storageAvailable) {
        console.warn('⚠️ localStorage is not accessible (likely in sandboxed context like theme editor)');
        return [];
      }

      try {
        const raw = localStorage.getItem(this.storageKey);
        console.log('Raw localStorage value:', raw);

        if (raw) {
          const parsed = JSON.parse(raw);
          console.log('Parsed data:', parsed);
          console.log('Number of products:', parsed.length);
          parsed.forEach((p, idx) => {
            console.log(`  ${idx + 1}. ${p.title} (ID: ${p.id})`);
          });
        } else {
          console.log('No data in localStorage yet');
        }
      } catch (error) {
        console.error('Error reading localStorage:', error);
      }

      return this.getRecentlyViewed();
    }
  };
}

// Initialize on page load - Use singleton pattern
function initializeRecentlyViewed() {
  // Only initialize once
  if (window.recentlyViewedManager) {
    console.log('⚠️ RecentlyViewedManager already initialized, skipping...');
    return;
  }

  console.log('🎬 Initializing Recently Viewed Manager...');
  console.log('📍 Document ready state:', document.readyState);
  console.log('📍 Product JSON element exists?', !!document.querySelector('[data-product-json]'));

  window.recentlyViewedManager = new window.RecentlyViewedManager();
  window.recentlyViewedManager.init();

  // Make it accessible for debugging
  console.log('💡 Testing commands:');
  console.log('   - Check storage: window.recentlyViewedManager.debugStorage()');
  console.log('   - Add test data: window.recentlyViewedManager.testAddMultipleProducts()');
  console.log('   - View products: window.recentlyViewedManager.getRecentlyViewed()');
  console.log('   - Clear all: window.recentlyViewedManager.clearRecentlyViewed()');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRecentlyViewed);
} else {
  // Document is already ready, but wait a bit for deferred scripts
  setTimeout(initializeRecentlyViewed, 100);
}
